/**
 * Eén regel voor het ordenen van prijshistoriek en het bepalen van de huidige prijs.
 *
 * De regel, in deze volgorde:
 *  1. de **ingangsdatum** bepaalt welke prijs geldt — de laatste ingangsdatum die
 *     bereikt is;
 *  2. hebben twee prijzen **dezelfde ingangsdatum**, dan geldt de **laatst
 *     ingevoerde**.
 *
 * Dit is dezelfde regel als `extractActivePrice` in
 * `yallyhub-be/src/shared/helpers/helpers.service.ts`, dat `activePrice` /
 * `activeProvision` op het contract vult, het Verhuur-overzicht voedt en naar Odoo
 * geduwd wordt. Wijk hier niet van af zonder de backend mee te wijzigen, anders
 * lopen de bedragen in YallyHub, de indexatiemail en Odoo weer uiteen.
 *
 * Waarom dit bestaat: de prijshistoriek-modal had drie eigen kopieën van een andere
 * vergelijking, die bij een gelijke ingangs- én registratiedatum terugviel op "de
 * hoogste prijs wint". Twee wijzigingen op dezelfde dag (eerst EUR 177,82, daarna
 * EUR 85,41) leverden daardoor de OUDSTE als "Huidig" op.
 */

export interface PriceLike {
  id: number;
  amount: number;
  effectiveDate?: string | Date | null;
  recordDate?: string | Date | null;
  /** Precies registratiemoment. Bij oude rijen enkel een datum (middernacht). */
  createdAt?: string | Date | null;
}

const time = (value: string | Date | null | undefined): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Of een ingangsdatum een echte datum is.
 *
 * Een jaartal vóór 1900 is geen huurdatum maar een typfout — er staat een rij in
 * productie met `0026-01-27` in plaats van `2026-01-27` (prijs 2585 op unit
 * 2400-COLL-18-7). Zo'n datum mag niet als "de oudste" meegeteld worden, want dan
 * verliest de nieuwste prijs van een oudere: precies de bug waardoor de
 * indexatiemail EUR 55 toonde terwijl YallyHub en Odoo EUR 20 hadden.
 */
const hasUsableEffectiveDate = (
  value: string | Date | null | undefined,
): boolean => {
  const parsed = time(value);
  return parsed !== null && new Date(parsed).getFullYear() >= 1900;
};

/**
 * Vergelijkt twee prijzen op ingangsdatum, of geeft null wanneer die vergelijking
 * niets zegt: gelijke datums, of een datum die niet te vertrouwen is. De aanroeper
 * valt dan terug op de invoerorde.
 */
const compareEffectiveDate = (
  a: PriceLike,
  b: PriceLike,
  direction: "desc" | "asc",
): number | null => {
  const aUsable = hasUsableEffectiveDate(a.effectiveDate);
  const bUsable = hasUsableEffectiveDate(b.effectiveDate);

  // Onbetrouwbare datum: niet op datum vergelijken, maar op invoerorde.
  if (!aUsable && !bUsable) return null;
  if (!aUsable || !bUsable) {
    const aHasAny = !!a.effectiveDate;
    const bHasAny = !!b.effectiveDate;
    // Een rij zonder énige datum is de initiële prijs en hoort onderaan (desc)
    // respectievelijk vooraan (asc). Een rij met een onzinnige datum niet: die
    // hoort op zijn invoermoment beoordeeld te worden.
    if (!aHasAny || !bHasAny) {
      const initialFirst = direction === "asc" ? -1 : 1;
      if (!aHasAny && bHasAny) return initialFirst;
      if (aHasAny && !bHasAny) return -initialFirst;
      return null;
    }
    return null;
  }

  const aTime = time(a.effectiveDate)!;
  const bTime = time(b.effectiveDate)!;
  if (aTime === bTime) return null;

  return direction === "desc" ? bTime - aTime : aTime - bTime;
};

/**
 * Nieuwste registratie eerst.
 *
 * `createdAt` is sinds de timestamp-migratie exact; voor rijen van daarvoor staat
 * daar middernacht, waardoor twee wijzigingen op dezelfde dag gelijk uitkomen. Het
 * id is dan de tiebreaker — een oplopende sequence, dus altijd de invoegorde.
 */
export const byRegistrationDesc = (a: PriceLike, b: PriceLike): number => {
  const aCreated = time(a.createdAt);
  const bCreated = time(b.createdAt);

  if (aCreated !== null && bCreated !== null && aCreated !== bCreated) {
    return bCreated - aCreated;
  }

  return b.id - a.id;
};

/** Of de ingangsdatum bereikt is. Zonder ingangsdatum geldt de prijs meteen. */
export const isEffectiveOn = (
  price: PriceLike,
  reference: Date = new Date(),
): boolean => {
  if (!price.effectiveDate) return true;

  const effective = new Date(price.effectiveDate);
  if (Number.isNaN(effective.getTime())) return true;

  const day = new Date(reference);
  day.setHours(23, 59, 59, 999);
  return effective <= day;
};

/**
 * De prijs die vandaag geldt.
 *
 * Van de prijzen waarvan de ingangsdatum bereikt is: die met de LAATSTE
 * ingangsdatum. Delen er meerdere die datum, dan de LAATST INGEVOERDE. Null wanneer
 * er nog geen enkele ingegaan is.
 */
export const findActivePrice = <T extends PriceLike>(
  prices: T[] | undefined | null,
  reference: Date = new Date(),
): T | null => {
  if (!prices || prices.length === 0) return null;

  const effective = prices.filter((price) => isEffectiveOn(price, reference));
  if (effective.length === 0) return null;

  return [...effective].sort((a, b) => {
    const byDate = compareEffectiveDate(a, b, "desc");
    return byDate !== null ? byDate : byRegistrationDesc(a, b);
  })[0] ?? null;
};

/**
 * Volgorde voor de prijshistoriek-tabel: laatste ingangsdatum bovenaan, en binnen
 * dezelfde ingangsdatum de laatst geregistreerde wijziging bovenaan. Toekomstige
 * prijzen staan dus boven de huidige, wat ze ook zijn: wat er staat aan te komen.
 */
export const sortPriceHistory = <T extends PriceLike>(prices: T[]): T[] =>
  [...prices].sort((a, b) => {
    const byDate = compareEffectiveDate(a, b, "desc");
    return byDate !== null ? byDate : byRegistrationDesc(a, b);
  });

/**
 * De basisprijs: de OUDSTE prijs — vroegste ingangsdatum (een rij zonder datum is de
 * initiële en gaat voorop), en bij een gelijke datum de eerst ingevoerde. Zo
 * verschuift de basisprijs niet mee met een latere correctie.
 */
export const findBasePrice = <T extends PriceLike>(
  prices: T[] | undefined | null,
): T | null => {
  if (!prices || prices.length === 0) return null;

  return [...prices].sort((a, b) => {
    const byDate = compareEffectiveDate(a, b, "asc");
    // Zelfde (of onbruikbare) ingangsdatum: de eerst ingevoerde.
    return byDate !== null ? byDate : byRegistrationDesc(b, a);
  })[0] ?? null;
};

/**
 * Registratiemoment met tijd wanneer die bekend is.
 *
 * Twee wijzigingen op dezelfde dag waren niet van elkaar te onderscheiden zolang
 * enkel de datum werd bewaard; met de tijd erbij is de volgorde in de UI zichtbaar
 * en niet alleen intern juist.
 */
export const formatRegistrationMoment = (price: PriceLike): string => {
  const created = price.createdAt ? new Date(price.createdAt) : null;
  const record = price.recordDate ? new Date(price.recordDate) : null;

  const base = record && !Number.isNaN(record.getTime()) ? record : created;
  if (!base || Number.isNaN(base.getTime())) return "-";

  const datePart = base.toLocaleDateString("nl-BE");

  // Middernacht = geen tijd bewaard (of toevallig middernacht); dan niets tonen,
  // een "00:00" suggereert een precisie die er niet is.
  if (
    created &&
    !Number.isNaN(created.getTime()) &&
    (created.getHours() !== 0 ||
      created.getMinutes() !== 0 ||
      created.getSeconds() !== 0)
  ) {
    const timePart = created.toLocaleTimeString("nl-BE", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart} ${timePart}`;
  }

  return datePart;
};
